import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, DollarSign, Users, TrendingUp, Wallet, ArrowRight, Loader2, Send, Clock, XCircle, Award } from 'lucide-react';
import { getMyReferral, getMyReferralEarnings, requestReferralPayout, applyForReferral } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';
import LoadingScreen from '../../components/layout/LoadingScreen';

interface Earning {
  id: number;
  referred_name: string;
  course_title: string;
  purchase_amount: number;
  commission_amount: number;
  status: string;
  created_at: string;
}

interface ReferralStats {
  approved: boolean;
  code?: string;
  total_referrals?: number;
  total_earned?: number;
  total_pending?: number;
  total_paid?: number;
  available_balance?: number;
  share_link?: string;
  application?: {
    status: string;
    reason: string;
    created_at: string;
    review_notes: string;
  } | null;
}

export default function ReferralDashboard(): React.JSX.Element {
  const toast = useToast();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('upi');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applyReason, setApplyReason] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, earningsRes] = await Promise.all([
        getMyReferral(),
        getMyReferralEarnings(),
      ]);
      const s = statsRes as any;
      setStats({
        approved: s.approved ?? false,
        code: s.code,
        total_referrals: s.total_referrals,
        total_earned: s.total_earned,
        total_pending: s.total_pending,
        total_paid: s.total_paid,
        available_balance: s.available_balance,
        share_link: s.share_link,
        application: s.application || null,
      });
      setEarnings((earningsRes as any).earnings || []);
    } catch (e: any) {
      toast.show(e.message || 'Failed to load referral data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!stats?.share_link) return;
    navigator.clipboard.writeText(stats.share_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.show('Link copied to clipboard!', 'success');
  };

  const handleApply = async () => {
    if (!applyReason.trim()) {
      toast.show('Please tell us why you want to join', 'error');
      return;
    }
    setApplying(true);
    try {
      await applyForReferral({ reason: applyReason.trim() });
      toast.show('Application submitted! We will review it shortly.', 'success');
      setApplyReason('');
      loadData();
    } catch (e: any) {
      toast.show(e.message || 'Failed to submit application', 'error');
    } finally {
      setApplying(false);
    }
  };

  const handlePayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) {
      toast.show('Please enter a valid amount', 'error');
      return;
    }
    if (amount > (stats?.available_balance || 0)) {
      toast.show('Amount exceeds available balance', 'error');
      return;
    }
    if (!payoutDetails.trim()) {
      toast.show('Please enter payment details', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await requestReferralPayout({
        amount,
        payment_method: payoutMethod,
        payment_details: { detail: payoutDetails },
      });
      toast.show('Payout request submitted!', 'success');
      setPayoutAmount('');
      setPayoutDetails('');
      loadData();
    } catch (e: any) {
      toast.show(e.message || 'Failed to request payout', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen message="Loading referral data..." />;

  // Not approved — show application flow
  if (!stats?.approved) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4">
                Refer & Earn
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Referral Program</h1>
              <p className="text-slate-600 max-w-xl mx-auto">
                Earn <strong>10% commission</strong> on every successful course purchase made by people you refer.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12">
          {/* Status card */}
          {stats?.application ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-slate-200 p-8 text-center mb-8"
            >
              {stats.application.status === 'pending' ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-amber-500" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Application Under Review</h2>
                  <p className="text-slate-600 mb-4">
                    You applied on {new Date(stats.application.created_at).toLocaleDateString()}. Our team is reviewing your application.
                  </p>
                  {stats.application.reason && (
                    <div className="bg-slate-50 rounded-xl p-4 text-left text-sm text-slate-600">
                      <span className="font-medium">Your reason:</span> {stats.application.reason}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Application Not Approved</h2>
                  <p className="text-slate-600 mb-4">
                    Unfortunately, your application was not approved at this time.
                  </p>
                  {stats.application.review_notes && (
                    <div className="bg-red-50 rounded-xl p-4 text-left text-sm text-red-700">
                      <span className="font-medium">Admin note:</span> {stats.application.review_notes}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-slate-200 p-8"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-amber-500" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Apply to Join</h2>
                <p className="text-slate-600 text-sm">
                  Tell us why you'd like to be a part of our referral program. Approved members get a unique referral link and earn 10% on every successful purchase.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Why do you want to join?</label>
                  <textarea
                    value={applyReason}
                    onChange={(e) => setApplyReason(e.target.value)}
                    placeholder="I have a network of finance professionals who would benefit from these courses..."
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  />
                </div>
                <Button onClick={handleApply} disabled={applying} className="w-full">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit Application</>}
                </Button>
              </div>
            </motion.div>
          )}

          {/* How it works */}
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[
              { step: '1', title: 'Apply', desc: 'Submit your application for review' },
              { step: '2', title: 'Share', desc: 'Get your unique referral link upon approval' },
              { step: '3', title: 'Earn', desc: 'Receive 10% commission on every purchase' },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
                <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Approved — show full dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4">
              Refer & Earn
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Your Referral Dashboard</h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              You are an approved referral partner. Earn <strong>10% commission</strong> on every successful course purchase.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Share Link */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Your Referral Link</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 truncate">
              {stats.share_link}
            </div>
            <Button onClick={handleCopy} variant="primary" className="flex items-center gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Your code: <span className="font-mono font-bold text-slate-700">{stats.code}</span>
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { icon: Users, label: 'Total Referrals', value: stats.total_referrals ?? 0, color: 'text-blue-600', bg: 'bg-blue-50' },
            { icon: DollarSign, label: 'Total Earned', value: `₹${(stats.total_earned ?? 0).toLocaleString('en-IN')}`, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { icon: TrendingUp, label: 'Pending', value: `₹${(stats.total_pending ?? 0).toLocaleString('en-IN')}`, color: 'text-amber-600', bg: 'bg-amber-50' },
            { icon: Wallet, label: 'Available', value: `₹${(stats.available_balance ?? 0).toLocaleString('en-IN')}`, color: 'text-purple-600', bg: 'bg-purple-50' },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center mb-3`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Earnings History */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">Earnings History</h3>
              </div>
              {earnings.length === 0 ? (
                <div className="p-8 text-center">
                  <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No earnings yet. Start sharing your link!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Referred User</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Course</th>
                        <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Purchase</th>
                        <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Commission</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {earnings.map((e) => (
                        <tr key={e.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-slate-900">{e.referred_name || 'User'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{e.course_title || 'Course'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600 text-right">₹{Number(e.purchase_amount).toLocaleString('en-IN')}</td>
                          <td className="px-6 py-4 text-sm font-bold text-emerald-600 text-right">₹{Number(e.commission_amount).toLocaleString('en-IN')}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                              e.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                              e.status === 'approved' ? 'bg-amber-50 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>{e.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Payout Request */}
          <div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Request Payout</h3>
              <div className="mb-4 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">Available Balance</p>
                <p className="text-2xl font-bold text-slate-900">₹{(stats.available_balance ?? 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹)</label>
                  <input type="number" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder="Enter amount" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label>
                  <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500">
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Details</label>
                  <textarea value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)} placeholder={payoutMethod === 'upi' ? 'yourname@upi' : 'Account number, IFSC, Bank name'} rows={3} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500" />
                </div>
                <Button onClick={handlePayout} disabled={submitting || (stats.available_balance || 0) <= 0} className="w-full">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Request Payout <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
